import { useEffect, useMemo, useState } from "react";
import type { JSX } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { toast } from "sonner";
import type { RecipeApiItem } from "@recipevault/shared";
import { Button } from "../ui/button.js";
import { Card, CardContent } from "../ui/card.js";
import { parseSharePayload, hostnameOf } from "../features/imports/shareText.js";
import { submitUrlImport } from "../features/imports/api.js";
import { findDuplicateByUrl } from "../features/imports/duplicates.js";
import { DuplicateNote } from "../features/imports/DuplicateNote.js";
import { ApiError } from "../shared/http.js";

const MIN_TEXT_LENGTH = 20;

/**
 * Android Web Share Target landing screen. A recipe shared from another app
 * (browser, Instagram, etc.) hits `/share?title=&text=&url=` (manifest
 * `share_target`, GET). This screen funnels straight into the existing
 * `POST /imports` pipeline, with a duplicate-import check against the local
 * Dexie recipe cache (Task 9).
 */
export function ShareTarget(): JSX.Element {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [inFlight, setInFlight] = useState(false);
  const [duplicate, setDuplicate] = useState<RecipeApiItem | null>(null);
  const searchString = searchParams.toString();

  // Parse once per distinct share payload, not on every render — Android can
  // re-fire the same share intent (e.g. on resume from background), and we
  // don't want that to recompute/re-trigger anything downstream.
  const payload = useMemo(() => parseSharePayload(searchParams), [searchString]);

  const isTextRedirect = payload.url === null && payload.text.trim().length >= MIN_TEXT_LENGTH;

  useEffect(() => {
    if (isTextRedirect) {
      void navigate("/imports/new?from=text", {
        state: { prefillText: payload.text },
        replace: true,
      });
    }
  }, [isTextRedirect, payload.text, navigate]);

  useEffect(() => {
    setDuplicate(null);
    if (payload.url === null) return;
    let cancelled = false;
    void findDuplicateByUrl(payload.url).then((dup) => {
      if (!cancelled) setDuplicate(dup);
    });
    return () => {
      cancelled = true;
    };
  }, [payload.url]);

  async function handleImport(): Promise<void> {
    if (inFlight || payload.url === null) return;
    setInFlight(true);
    try {
      const jobId = await submitUrlImport(payload.url);
      await navigate(`/imports/${encodeURIComponent(jobId)}`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 402) {
        toast.error("Monthly cap reached. See Settings → Household.");
      } else {
        toast.error(err instanceof Error ? err.message : "Import failed");
      }
      setInFlight(false);
    }
  }

  if (payload.url !== null) {
    const url = payload.url;
    const hostname = hostnameOf(url);
    return (
      <div className="mx-auto max-w-xl px-4 py-6 space-y-6">
        <Card>
          <CardContent className="space-y-4 pt-6">
            <p className="text-lg font-medium">
              Import recipe from <strong className="text-primary">{hostname}</strong>?
            </p>
            <p className="break-all text-sm text-muted-foreground">{url}</p>
            {duplicate && <DuplicateNote title={duplicate.title} />}
            <div className="flex gap-3">
              <Button
                type="button"
                className="min-h-12 flex-1"
                disabled={inFlight}
                onClick={() => void handleImport()}
              >
                {inFlight ? "Importing…" : duplicate ? "Import anyway" : "Import"}
              </Button>
              {duplicate ? (
                <Button asChild variant="outline" className="min-h-12 flex-1">
                  <Link to={`/recipes/${duplicate.id}`}>Open recipe</Link>
                </Button>
              ) : (
                <Button asChild variant="ghost" className="min-h-12 flex-1">
                  <Link to="/">Cancel</Link>
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isTextRedirect) {
    // Redirect fires from the effect above; this is the brief instant before it lands.
    return (
      <div className="mx-auto max-w-xl px-4 py-6 text-sm text-muted-foreground">
        Preparing import…
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-xl px-4 py-6 space-y-4">
      <p className="text-muted-foreground">Nothing to import in that share.</p>
      <Link to="/recipes/new" className="text-sm text-primary underline underline-offset-2">
        New recipe →
      </Link>
    </div>
  );
}
